// src/services/questionFactory/providers/pmiInfinityProvider.js
//
// PMI Infinity generation provider. Phase 2b — wired live.
// Calls the shared question-factory-generate edge function which
// dispatches by `provider: 'pmi_infinity'` and holds the JWT
// PMI_INFINITY_TOKEN server-side.
//
// Infinity is generation-only — there's no scoring API surface, so
// review() throws ProviderError.

import { supabase } from '../../supabaseClient';
import { ProviderError, normalizeGenerateOpts } from './providerBase';

const PROVIDER_ID   = 'pmi_infinity';
const DISPLAY_NAME  = 'PMI Infinity';
const DEFAULT_MODEL = 'pmi-infinity-default';

async function generate(opts = {}) {
  if (!opts.runId) {
    throw new ProviderError('generate(): runId is required', { provider: PROVIDER_ID });
  }
  const o = normalizeGenerateOpts(opts);

  const payload = {
    run_id:             opts.runId,
    provider:           PROVIDER_ID,
    exam_id:            o.examId,
    count:              o.count,
    domain:             o.domain,
    model:              opts.model || DEFAULT_MODEL,
    difficulty:         opts.difficulty || null,
    extra_instructions: opts.extraInstructions || null,
  };

  const { data, error } = await supabase.functions.invoke('question-factory-generate', { body: payload });
  if (error) {
    let detail = error.message || String(error);
    try {
      if (error.context && typeof error.context.text === 'function') {
        const txt = await error.context.text();
        if (txt) detail = `${detail} — ${txt}`;
      }
    } catch { /* swallow */ }
    throw new ProviderError(`PMI Infinity generate failed: ${detail}`, { provider: PROVIDER_ID, cause: error });
  }
  if (!data)        throw new ProviderError('PMI Infinity generate: empty response', { provider: PROVIDER_ID });
  if (data.error)   throw new ProviderError(`PMI Infinity generate: ${data.error}`,   { provider: PROVIDER_ID });

  return {
    inserted:     Number(data.items_inserted || 0),
    failed:       Number(data.items_failed   || 0),
    modelUsed:    data.model_used || DEFAULT_MODEL,
    inputTokens:  Number(data.input_tokens  || 0),
    outputTokens: Number(data.output_tokens || 0),
    warnings:     Array.isArray(data.warnings) ? data.warnings : [],
    items:        Array.isArray(data.items) ? data.items : [],
  };
}

async function review(/* opts */) {
  throw new ProviderError('PMI Infinity does not expose a scoring API; use Claude or ChatGPT for adjudication.', {
    provider: PROVIDER_ID,
  });
}

// PMI Infinity is reachable through scripts/pmi-infinity-submit.js running
// locally on the admin's machine, but Cloudflare in front of
// infinity-api.pmi.org serves a 'Just a moment' challenge to any
// server-to-server request from a datacenter IP (including Supabase Edge
// Function and Netlify Function runtimes). Until we find a path through CF
// bot management, the in-app provider stays disabled and admins use the
// CLI script + bulk-import to bring Infinity-sourced content into the bank.
const pmiInfinityProviderModule = {
  id:             PROVIDER_ID,
  displayName:    DISPLAY_NAME,
  enabled:        false,
  cliOnly:        true,
  cliNote:        'PMI Infinity blocks server-to-server requests via Cloudflare. Generate via scripts/pmi-infinity-submit.js, then use Bulk Import.',
  supportedModes: ['generate'],
  defaultModel:   DEFAULT_MODEL,
  generate,
  review,
};

export default pmiInfinityProviderModule;
